import React, { useState, useEffect } from 'react';

// Components and Others
import PropTypes from 'prop-types';
import ButtonStyles from 'styles/theme/Button';
import { makeStyles } from '@material-ui/core/styles';
import {
    DialogContent,
    Dialog,
    Grid,
    Typography,
} from '@material-ui/core';
import MailOutlineIcon from '@material-ui/icons/MailOutline';
import PhoneOutlinedIcon from '@material-ui/icons/PhoneOutlined';
import LocationOnOutlinedIcon from '@material-ui/icons/LocationOnOutlined';
import DialogAppBar from 'components/widgets/modal/DialogAppBar';
import DialogActions from 'components/widgets/modal/DialogActions';
import PhoneUtils from 'lib/PhoneUtils';
import moment from 'moment';

const useStyles = makeStyles((theme) => ({
    dialogContent: {
        padding: theme.spacing(3),
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
    },
    headerText: {
        fontWeight: 'bold',
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(1),
        textTransform: 'uppercase',
        fontSize: '0.8rem',
    },
    enteredCustomerCard: {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        borderRadius: '8px',
        padding: theme.spacing(2),
    },
    enteredName: {
        fontWeight: 'bold',
        fontSize: '1.2rem',
        marginBottom: theme.spacing(2),
        color: theme.palette.primary.contrastText,
    },
    infoRow: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: theme.spacing(1.5),
        fontSize: '0.9rem',
        '& svg': {
            marginRight: theme.spacing(1),
            fontSize: '1.2rem',
            opacity: 0.8,
        },
    },
    duplicateList: {
        maxHeight: '360px',
        overflowY: 'auto',
        paddingRight: theme.spacing(1),
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
        '&::-webkit-scrollbar': {
            width: '6px',
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: theme.palette.grey[400],
            borderRadius: '4px',
        },
    },
    duplicateCard: {
        border: `1px solid ${theme.palette.grey[300]}`,
        borderRadius: '8px',
        padding: theme.spacing(2),
        cursor: 'pointer',
        transition: 'all 0.2s',
        backgroundColor: theme.palette.background.paper,
        minHeight: 110,
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        '&:hover': {
            borderColor: theme.palette.primary.main,
        },
    },
    duplicateCardSelected: {
        border: `1px solid ${theme.palette.primary.main}`,
        backgroundColor: theme.palette.action.selected,
    },
    dupHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: theme.spacing(1),
    },
    dupName: {
        fontSize: '1rem',
        color: theme.palette.text.primary,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        paddingRight: theme.spacing(1),
    },
    customerLink: {
        marginLeft: theme.spacing(1),
    },
    matchTag: {
        padding: '4px 8px',
        borderRadius: '4px',
        fontSize: '0.75rem',
        fontWeight: 'bold',
        flexShrink: 0,
    },
    matchHigh: {
        backgroundColor: theme.palette.success.light,
        color: theme.palette.success.dark,
    },
    matchMed: {
        backgroundColor: theme.palette.info.light,
        color: theme.palette.info.dark,
    },
    matchLow: {
        backgroundColor: theme.palette.grey[100],
        color: theme.palette.grey[700],
    },
    dupDetails: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(0.5),
        color: theme.palette.text.secondary,
        fontSize: '0.85rem',
        flex: 1,
    },
    dupContactRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1),
    },
    dupContact: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
        minWidth: 0,
        overflow: 'hidden',
        '& svg': {
            fontSize: '1rem',
            marginRight: '4px',
            verticalAlign: 'text-bottom',
        },
    },
    dupAddressRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1),
        marginTop: 'auto',
    },
    dupAddress: {
        display: 'flex',
        alignItems: 'center',
        minWidth: 0,
        flex: 1,
        overflow: 'hidden',
        '& svg': {
            fontSize: '1rem',
            marginRight: '4px',
            flexShrink: 0,
        },
    },
    addressText: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    businessLabel: {
        fontSize: '0.7rem',
        fontWeight: 'bold',
        textTransform: 'uppercase',
        color: theme.palette.primary.main,
        flexShrink: 0,
    },
    ...ButtonStyles.getStyle(theme),
}));

const SuggestCustomer = ({
    open, onClose, suggestedCustomer, enteredCustomer,
    onUseEntered, onUseSuggested, isCustomerModule,
}) => {
    const classes = useStyles();
    const [selectedIndex, setSelectedIndex] = useState(null);

    useEffect(() => {
        if (open) {
            setSelectedIndex(null);
        }
    }, [open, suggestedCustomer]);

    const empty = '--';

    let customers = [];
    if (Array.isArray(suggestedCustomer)) {
        customers = suggestedCustomer;
    } else if (suggestedCustomer) {
        customers = [suggestedCustomer];
    }

    const activeCustomer = selectedIndex !== null ? customers[selectedIndex] : null;

    const renderMatchTag = (matchValue) => {
        const pct = matchValue || 0;
        let tagClass = classes.matchLow;
        if (pct >= 90) tagClass = classes.matchHigh;
        else if (pct >= 75) tagClass = classes.matchMed;

        return (
            <div className={`${classes.matchTag} ${tagClass}`}>
                {pct}
                % MATCH
            </div>
        );
    };

    const renderDate = (customer) => {
        if (customer.modifiedOn) {
            return `Updated ${moment(customer.modifiedOn).fromNow()}`;
        }
        if (customer.sysDate) {
            return `Created ${moment(customer.sysDate).fromNow()}`;
        }
        return '';
    };

    const getFullName = (customerObj) => {
        if (!customerObj) return empty;
        return [customerObj.firstName, customerObj.lastName].filter(Boolean).join(' ') || empty;
    };

    const enteredFullName = getFullName(enteredCustomer);

    const titlePrimary = isCustomerModule ? 'Open Suggested Customer' : 'Use Suggested Customer';

    return (
        <Dialog
            open={open}
            fullWidth
            maxWidth="md"
            aria-label="Suggested Customer"
        >
            <DialogAppBar title="Suggested Customer" onClose={onClose} iconSize="sm" />
            <DialogContent className={classes.dialogContent}>
                <Grid container spacing={3}>
                    {/* LEFT COLUMN: ENTERED CUSTOMER */}
                    <Grid item xs={12} sm={5}>
                        <Typography className={classes.headerText}>Entered Customer</Typography>
                        <div className={classes.enteredCustomerCard}>
                            <Typography className={classes.enteredName}>{enteredFullName}</Typography>
                            <div className={classes.infoRow}>
                                <MailOutlineIcon />
                                <span>{enteredCustomer?.email || empty}</span>
                            </div>
                            <div className={classes.infoRow}>
                                <PhoneOutlinedIcon />
                                <span>{PhoneUtils.format(enteredCustomer?.cellPhone) || empty}</span>
                            </div>
                            <div className={classes.infoRow}>
                                <LocationOnOutlinedIcon />
                                <span>{enteredCustomer?.address1 || empty}</span>
                            </div>
                        </div>
                    </Grid>

                    {/* RIGHT COLUMN: POTENTIAL DUPLICATES */}
                    <Grid item xs={12} sm={7}>
                        <Typography className={classes.headerText}>
                            Potential Duplicates Found (
                            {customers.length}
                            )
                        </Typography>
                        <div className={classes.duplicateList}>
                            {customers.length === 0 && (
                                <Typography color="textSecondary">No customer found</Typography>
                            )}
                            {customers.map((c, index) => {
                                const isSelected = selectedIndex === index;
                                const fullName = getFullName(c);
                                const cellPhone = PhoneUtils.format(c.cellPhone) || empty;

                                return (
                                    <div
                                        key={c.customerId || index}
                                        role="button"
                                        tabIndex={0}
                                        className={`${classes.duplicateCard} ${isSelected ? classes.duplicateCardSelected : ''}`}
                                        onClick={() => setSelectedIndex(index)}
                                        onKeyDown={(e) => {
                                            if (e.target !== e.currentTarget) return;
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                setSelectedIndex(index);
                                            }
                                        }}
                                    >
                                        <div className={classes.dupHeader}>
                                            <div className={classes.dupName}>
                                                {fullName}
                                                {c.customerCode && (
                                                    <a
                                                        href={`/customers/${c.customerCode}`}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className={`${classes.customerLink} link`}
                                                        onClick={(e) => e.stopPropagation()}
                                                    >
                                                        #
                                                        {c.customerCode}
                                                    </a>
                                                )}
                                            </div>
                                            {renderMatchTag(c.matchPercentage)}
                                        </div>
                                        <div className={classes.dupDetails}>
                                            <div className={classes.dupContactRow}>
                                                <div className={classes.dupContact}>
                                                    <span>
                                                        <MailOutlineIcon />
                                                        {' '}
                                                        {c.email || empty}
                                                    </span>
                                                    <span>&bull;</span>
                                                    <span>
                                                        <PhoneOutlinedIcon />
                                                        {' '}
                                                        {cellPhone}
                                                    </span>
                                                </div>
                                                <div>
                                                    {renderDate(c)}
                                                </div>
                                            </div>
                                            <div className={classes.dupAddressRow}>
                                                <div className={classes.dupAddress}>
                                                    <LocationOnOutlinedIcon />
                                                    <span className={classes.addressText}>
                                                        {c.address1 || empty}
                                                    </span>
                                                </div>
                                                {c.isBusiness && (
                                                    <div className={classes.businessLabel}>Business</div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions
                titlePrimary={titlePrimary}
                titleSecondary="Use Entered Customer"
                onClickSecondary={onUseEntered}
                onClickPrimary={() => onUseSuggested(activeCustomer)}
                classSecondaryButton={classes.containedGreen}
                disablePrimaryButton={activeCustomer == null}
            />
        </Dialog>
    );
};

SuggestCustomer.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onUseEntered: PropTypes.func.isRequired,
    onUseSuggested: PropTypes.func.isRequired,
    suggestedCustomer: PropTypes.oneOfType([
        PropTypes.object,
        PropTypes.array,
    ]).isRequired,
    enteredCustomer: PropTypes.object,
    isCustomerModule: PropTypes.bool,
};

SuggestCustomer.defaultProps = {
    isCustomerModule: false,
    enteredCustomer: {},
};

export default SuggestCustomer;
